// macadamia-app/src/macadamia-core/components/widgets/TitleWidget.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useDossier } from '../../dossierEditor/DossierContext';
import EllipsisVertical from '../../icons/EllipsisVertical';
import Bars3 from '../../icons/Bars3';
import Bars3BottomLeft from '../../icons/Bars3BottomLeft';
import Bars3BottomRight from '../../icons/Bars3BottomRight';
import '../styles/TitleWidget.css';

// Configuration centralisée des styles pour chaque niveau
const TITLE_STYLES = {
    1: { fontSize: '32px', lineHeight: '41px', minHeight: '41px' },
    2: { fontSize: '28px', lineHeight: '36px', minHeight: '36px' },
    3: { fontSize: '24px', lineHeight: '31px', minHeight: '31px' },
    4: { fontSize: '20px', lineHeight: '26px', minHeight: '26px' },
    5: { fontSize: '18px', lineHeight: '23px', minHeight: '23px' },
    6: { fontSize: '16px', lineHeight: '21px', minHeight: '21px' },
    7: { fontSize: '14px', lineHeight: '19px', minHeight: '19px' }
} as const;

interface TitleWidgetProps {
    title: string;
    level?: 1 | 2 | 3 | 4 | 5 | 6 | 7;
    alignment?: 'left' | 'center' | 'right';
    showInSummary?: boolean;
    onTitleChange: (newTitle: string) => void;
    onLevelChange?: (newLevel: 1 | 2 | 3 | 4 | 5 | 6 | 7) => void;
    onAlignmentChange?: (newAlignment: 'left' | 'center' | 'right') => void;
    onToggleSummary?: (show: boolean) => void;
}

const TitleWidget: React.FC<TitleWidgetProps> = ({
    title,
    level = 1,
    alignment = 'left',
    showInSummary = true,
    onTitleChange,
    onLevelChange,
    onAlignmentChange,
    onToggleSummary
}) => {
    const { editMode: currentState } = useDossier();
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [showLevelSelector, setShowLevelSelector] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const settingsRef = useRef<HTMLDivElement>(null);

    const currentStyle = TITLE_STYLES[level];

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onTitleChange(e.target.value);
    };

    const adjustHeight = () => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    };

    useEffect(() => {
        adjustHeight();
    }, [title, currentState, level]);

    useEffect(() => {
        if (currentState === 'editor') {
            adjustHeight();
            setTimeout(adjustHeight, 0);
        }
    }, [currentState]);

    // Fermer le dropdown si on clique ailleurs
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
                setShowSettings(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleLevelChange = (newLevel: 1 | 2 | 3 | 4 | 5 | 6 | 7) => {
        if (onLevelChange) {
            onLevelChange(newLevel);
        }
        setShowLevelSelector(false);
    };

    // Styles communs pour le textarea et le heading
    const commonStyles: React.CSSProperties = {
        fontSize: currentStyle.fontSize,
        lineHeight: currentStyle.lineHeight,
        minHeight: currentStyle.minHeight,
        border: '1px solid rgba(255, 255, 255, 0)',
        margin: 0,
        padding: 0,
        whiteSpace: 'pre-wrap',
        boxSizing: 'border-box',
        textAlign: alignment,
    };

    // Styles spécifiques au textarea
    const textareaStyles: React.CSSProperties = {
        ...commonStyles,
        width: '100%',
        resize: 'none',
        overflow: 'hidden',
        backgroundColor: 'transparent',
        outline: 'none',
    };

    // Rendu du heading approprié
    const renderHeading = () => {
        const HeadingTag = `h${level === 7 ? 6 : level}` as keyof JSX.IntrinsicElements; // H7 n'existe pas en HTML standard
        return (
            <HeadingTag
                style={{ ...commonStyles, fontSize: level === 7 ? '14px' : commonStyles.fontSize }} // Override for H7 visual
                className="title-widget-heading font-platypi dark:text-mcdm-primary-100"
            >
                {title}
            </HeadingTag>
        );
    };

    return (
        <div className="title-widget-container">
            {currentState === 'editor' ? (
                <div className="relative">
                    {/* Groupe de contrôles (Positionné en absolue via CSS .title-level-controls) */}
                    <div className="title-level-controls flex items-center gap-1">
                        {/* Sélecteur de niveau */}
                        {onLevelChange && (
                            <div className="relative">
                                <button
                                    onClick={() => setShowLevelSelector(!showLevelSelector)}
                                    className="title-level-button px-3 py-0.5 rounded-full border border-gray-300 bg-white hover:bg-gray-50 text-sm font-medium text-gray-700 transition"
                                    title="Changer le niveau du titre"
                                >
                                    Taille {level}
                                </button>

                                {showLevelSelector && (
                                    <div className="title-level-selector absolute top-full right-0 mt-1 bg-white dark:bg-mcdm-third-400 border border-gray-200 dark:border-mcdm-third-500 rounded-lg shadow-lg z-50 p-1 flex flex-col gap-1 w-48">
                                        {[1, 2, 3, 4, 5, 6, 7].map((lvl) => (
                                            <button
                                                key={lvl}
                                                onClick={() => handleLevelChange(lvl as any)}
                                                className={`text-left px-3 py-1.5 rounded font-platypi transition-colors ${lvl === level
                                                    ? 'bg-mcdm-secondary-100 text-mcdm-secondary-600 dark:bg-mcdm-secondary-900/50 dark:text-mcdm-secondary-400 font-bold'
                                                    : 'text-gray-700 dark:text-mcdm-primary-100 hover:bg-gray-100 dark:hover:bg-mcdm-third-300'
                                                    }`}
                                                style={{ fontSize: TITLE_STYLES[lvl as keyof typeof TITLE_STYLES].fontSize }}
                                            >
                                                Taille {lvl}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Dropdown Options */}
                        <div className="relative flex items-center" ref={settingsRef}>
                            <button
                                onClick={() => setShowSettings(!showSettings)}
                                className="rounded-full border border-gray-300 bg-white hover:bg-gray-50 text-gray-600 dark:bg-mcdm-third-400 dark:border-mcdm-third-300 dark:text-mcdm-primary-100 dark:hover:bg-mcdm-third-300 transition h-[26px] w-[26px] flex items-center justify-center p-0.5"
                                title="Options du titre"
                            >
                                <EllipsisVertical width="14" height="14" />
                            </button>

                            {showSettings && (
                                <div className="absolute top-full left-0 mt-1 bg-white dark:bg-mcdm-third-400 border border-gray-200 dark:border-mcdm-third-500 rounded-lg shadow-lg z-50 p-2 w-48 font-inter text-sm">
                                    <div className="mb-2">
                                        <p className="text-xs text-gray-400 dark:text-mcdm-primary-200 mb-1 uppercase tracking-wider font-semibold">Alignement</p>
                                        <div className="flex bg-gray-100 dark:bg-mcdm-third-500 rounded p-1 gap-1">
                                            <button
                                                onClick={() => onAlignmentChange && onAlignmentChange('left')}
                                                className={`flex-1 py-1 rounded text-center capitalize text-xs flex items-center justify-center ${alignment === 'left' ? 'bg-white shadow text-black dark:bg-mcdm-third-300 dark:text-mcdm-primary-100 font-medium' : 'text-gray-500 hover:text-gray-700 dark:text-mcdm-primary-200 dark:hover:text-white'}`}
                                                title="Aligner à gauche"
                                            >
                                                <Bars3BottomLeft width="16" height="16" />
                                            </button>
                                            <button
                                                onClick={() => onAlignmentChange && onAlignmentChange('center')}
                                                className={`flex-1 py-1 rounded text-center capitalize text-xs flex items-center justify-center ${alignment === 'center' ? 'bg-white shadow text-black dark:bg-mcdm-third-300 dark:text-mcdm-primary-100 font-medium' : 'text-gray-500 hover:text-gray-700 dark:text-mcdm-primary-200 dark:hover:text-white'}`}
                                                title="Centrer"
                                            >
                                                <Bars3 width="16" height="16" />
                                            </button>
                                            <button
                                                onClick={() => onAlignmentChange && onAlignmentChange('right')}
                                                className={`flex-1 py-1 rounded text-center capitalize text-xs flex items-center justify-center ${alignment === 'right' ? 'bg-white shadow text-black dark:bg-mcdm-third-300 dark:text-mcdm-primary-100 font-medium' : 'text-gray-500 hover:text-gray-700 dark:text-mcdm-primary-200 dark:hover:text-white'}`}
                                                title="Aligner à droite"
                                            >
                                                <Bars3BottomRight width="16" height="16" />
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-mcdm-third-300 p-1 rounded">
                                            <input
                                                type="checkbox"
                                                checked={!showInSummary}
                                                onChange={(e) => onToggleSummary && onToggleSummary(!e.target.checked)}
                                                className="rounded border-gray-300 text-mcdm-secondary-500 focus:ring-mcdm-secondary-500"
                                            />
                                            <span className="text-gray-700 dark:text-mcdm-primary-100">Ne pas inclure au sommaire</span>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>


                    {/* Textarea d'édition */}
                    <textarea
                        ref={textareaRef}
                        value={title}
                        onChange={(e) => {
                            handleChange(e);
                            adjustHeight();
                        }}
                        placeholder={`Titre de niveau ${level}`}
                        style={textareaStyles}
                        rows={1}
                        className="title-widget-editor font-platypi dark:text-mcdm-primary-100"
                    />
                </div>
            ) : (
                renderHeading()
            )}
        </div>
    );
};

export default TitleWidget;